'use client';
import React, { useCallback, useState } from 'react';
import subMonths from 'date-fns/subMonths';

import Layout from '../lib/components/Layout';
import CalendarService from '../lib/services/CalendarService';
import ColumnsDefinition from './ColumnsDef';
import { formatRequestDate } from '../lib/utils';

import { ICalendarProps, IDateRange } from '../lib/interfaces';
import { ShortInterest } from '@benzinga/quotes-manager';

export const ShortInterestCalendarColumnDef = ColumnsDefinition;

export const ShortInterestCalendar: React.FC<ICalendarProps> = ({
  calendar,
  calendarData,
  hasStockTableStyling,
  height,
  hideFilters,
  initialData,
  initialDateRange,
  initialTicker,
  onDataLoad,
}: ICalendarProps) => {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<ShortInterest[]>(initialData || []);
  let dateRange: IDateRange;

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(subMonths(new Date(), 1)),
      date_to: formatRequestDate(new Date()),
    };
  }

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setTableData(initialData);
    }
  }, [initialData, calendarData]);

  const fetchData = useCallback(
    (filterParams: any) => {
      const query = {
        ...filterParams,
        dateFrom: filterParams['parameters[date_from]'],
        dateTo: filterParams['parameters[date_to]'],
        symbols: filterParams['parameters[tickers]'],
      };

      setLoading(true);

      const Calendar = new CalendarService();

      Calendar.getShortInterests(query)
        .then(({ data }) => {
          const items: any = [];
          const shortInterestData = data.shortInterestData;

          if (shortInterestData) {
            Object.keys(shortInterestData).forEach(key => {
              if (shortInterestData[key].data) {
                items.push(...shortInterestData[key].data);
              }
            });
          }

          return items;
        })
        .then((items: any) => {
          setTableData(items);
          onDataLoad && onDataLoad(items);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [onDataLoad],
  );

  const onTableReady = useCallback(() => {
    if (initialData) {
      setTableData(initialData);
    } else {
      const params = {
        'parameters[date_from]': dateRange.date_from,
        'parameters[date_to]': dateRange.date_to,
      };
      if (initialTicker) {
        params['parameters[tickers]'] = initialTicker;
      }
      fetchData(params);
    }
  }, [dateRange.date_from, dateRange.date_to, fetchData, initialData, initialTicker]);

  return (
    <Layout
      calendar={calendar}
      calendarData={calendarData}
      columnDefs={ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      onFilterChanged={fetchData}
      onTableReady={onTableReady}
      tableData={tableData}
      tableLoading={loading}
      ticker={initialTicker}
    />
  );
};
